import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { CONSTANT } from "src/app/constant";
import { CandidateRole } from "src/app/models/enum";
import {
	AssessmentConfigRes,
	ReportSummary,
	Ts1V3ManeuverBreakdownItem,
	Ts1V3ManeuverBreakdownSection,
	Ts1V3ManeuverBreakdownSectionRow,
	Ts1V3ObCellState,
} from "src/app/models/models";
import {
	buildConditionalObKeySet,
	isConditionalObKey,
	TS1_CONDITIONAL_OB_BADGE_SRC,
	TS1_CONDITIONAL_OB_BADGE_TOOLTIP,
	TS1_PREDEFINED_OB_BADGE_TOOLTIP,
} from "src/app/cbta-web/ratings/container-ratings-form/ts1-rating-form-v3/utils/ts1-predefined-ob.utils";
import { buildSummaryReportTitle } from "src/app/shared/utils/form-type-label";
import { Ts1ReportPreviewContext } from "../ts1-report-preview.utils";

@Component({
	selector: "app-ts1-v3-maneuver-breakdown",
	templateUrl: "./ts1-v3-maneuver-breakdown.component.html",
	styleUrls: ["./ts1-v3-maneuver-breakdown.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class Ts1V3ManeuverBreakdownComponent implements OnChanges {
	@Input() reportSummary?: ReportSummary;
	@Input() previewContext: Ts1ReportPreviewContext | null = null;
	@Input() maneuverBreakdownView: Ts1V3ManeuverBreakdownItem[] = [];

	readonly CandidateRole = CandidateRole;
	readonly conditionalObBadgeSrc = TS1_CONDITIONAL_OB_BADGE_SRC;
	readonly predefinedObBadgeTooltip = TS1_PREDEFINED_OB_BADGE_TOOLTIP;
	readonly conditionalObBadgeTooltip = TS1_CONDITIONAL_OB_BADGE_TOOLTIP;
	private readonly expandedSectionKeys = new Set<string>();
	private readonly conditionalObKeysByManeuver = new Map<string, Set<string>>();

	get summaryReportTitle(): string {
		return buildSummaryReportTitle(this.reportSummary?.formType);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["maneuverBreakdownView"]) {
			this.rebuildExpandedSections();
		}
		if (changes["maneuverBreakdownView"] || changes["reportSummary"]) {
			this.rebuildConditionalObKeys();
		}
	}

	sectionHasValue(section: Ts1V3ManeuverBreakdownSection): boolean {
		return section.selectedCount > 0;
	}

	isSectionExpanded(maneuverCode: string, section: Ts1V3ManeuverBreakdownSection): boolean {
		return this.expandedSectionKeys.has(this.sectionKey(maneuverCode, section.code));
	}

	onSectionExpandedChange(maneuverCode: string, sectionCode: string, expanded: boolean): void {
		const key = this.sectionKey(maneuverCode, sectionCode);
		if (expanded) {
			this.expandedSectionKeys.add(key);
			return;
		}
		this.expandedSectionKeys.delete(key);
	}

	private sectionKey(maneuverCode: string, sectionCode: string): string {
		return `${maneuverCode}::${sectionCode}`;
	}

	private rebuildExpandedSections(): void {
		this.expandedSectionKeys.clear();
		for (const maneuver of this.maneuvers) {
			for (const section of maneuver.sections) {
				if (section.selectedCount > 0) {
					this.expandedSectionKeys.add(this.sectionKey(maneuver.maneuverCode, section.code));
				}
			}
		}
	}

	get maneuvers(): Ts1V3ManeuverBreakdownItem[] {
		return this.maneuverBreakdownView ?? [];
	}

	get leftCandidateName(): string {
		return this.resolveColumnCandidateName("pf");
	}

	get rightCandidateName(): string {
		return this.resolveColumnCandidateName("pm");
	}

	private resolveColumnCandidateName(_column: "pf" | "pm"): string {
		// Page 2 is viewing-candidate-scoped (PF + PM columns for this report only).
		const preview = this.reportSummary?.ts1V3PreviewSummary;
		return (
			preview?.candidateDetails?.name ??
			this.reportSummary?.candidateDetails?.name ??
			this.previewContext?.candidateNames?.[0] ??
			"—"
		);
	}

	get leftRoleLabel(): string {
		return "Pilot Flying";
	}

	get rightRoleLabel(): string {
		return "Pilot Monitoring";
	}

	formatManeuverOrder(order: number): string {
		return String(order).padStart(2, "0");
	}

	sectionRows(section: Ts1V3ManeuverBreakdownSection): Ts1V3ManeuverBreakdownSectionRow[] {
		if (section.rows?.length) return section.rows;
		return section.observations.map((observation) => ({
			pf: observation,
			pm: observation,
		}));
	}

	showAttemptCount(cell: Ts1V3ObCellState): boolean {
		return this.attemptCounts(cell).show;
	}

	attemptCounts(cell: Ts1V3ObCellState): {
		current: number;
		total: number;
		show: boolean;
	} {
		if (cell.isDisabled) {
			const show = cell.currentCount > 0 || cell.totalCount > 0;
			return {
				current: cell.currentCount,
				total: cell.totalCount,
				show,
			};
		}

		if (cell.isRemoved) {
			const total = cell.totalCount > 0 ? cell.totalCount : 0;
			const current = cell.currentCount;
			return {
				current,
				total,
				show: total > 0 || current > 0,
			};
		}

		let { currentCount: current, totalCount: total } = cell;
		if (current > 0 && total === 0) {
			total = current;
		}

		const isSelected = cell.isAdded || cell.lastAction === "pre-selected" || current > 0 || total > 0;
		if (isSelected && current === 0 && total === 0) {
			current = 1;
			total = 1;
		}

		return {
			current,
			total,
			show: isSelected,
		};
	}

	showPredefinedStar(cell: Ts1V3ObCellState): boolean {
		const lastAction = cell.lastAction ?? "-";
		const isGreyed = cell.isDisabled || lastAction === CONSTANT.OB_LAST_ACTION.DISABLED;
		return cell.isOriginal && !isGreyed;
	}

	shouldHighlightConditionalOb(
		maneuverCode: string,
		obLabel: string,
		role: CandidateRole,
	): boolean {
		const keys =
			this.conditionalObKeysByManeuver.get(maneuverCode) ??
			buildConditionalObKeySet(this.getAssessmentConfig(), maneuverCode);
		return isConditionalObKey(keys, obLabel, role);
	}

	cellStateClass(
		cell: Ts1V3ObCellState,
		maneuverCode: string,
		obLabel: string,
		role: CandidateRole,
	): Record<string, boolean> {
		const lastAction = cell.lastAction ?? "-";
		const isGreyed = cell.isDisabled || lastAction === CONSTANT.OB_LAST_ACTION.DISABLED;
		const isAdded = !isGreyed && (lastAction === "added" || (cell.isAdded && !cell.isRemoved));
		const isRemoved = !isGreyed && (lastAction === "removed" || (cell.isRemoved && !cell.isAdded));
		const isActive =
			!isGreyed &&
			!isAdded &&
			!isRemoved &&
			(cell.currentCount > 0 || lastAction === "pre-selected" || (cell.isOriginal && cell.currentCount > 0));
		return {
			"ts1-maneuver-breakdown__ob-cell--active": isActive,
			"ts1-maneuver-breakdown__ob-cell--added": isAdded,
			"ts1-maneuver-breakdown__ob-cell--removed": isRemoved,
			"ts1-maneuver-breakdown__ob-cell--greyed": isGreyed,
			"ts1-maneuver-breakdown__ob-cell--idle": !isGreyed && !isAdded && !isRemoved && !isActive,
		};
	}

	private getAssessmentConfig(): AssessmentConfigRes[] {
		return (this.reportSummary?.ts1V3PreviewSummary?.assessmentConfig ?? []) as AssessmentConfigRes[];
	}

	private rebuildConditionalObKeys(): void {
		this.conditionalObKeysByManeuver.clear();
		const assessmentConfig = this.getAssessmentConfig();
		for (const maneuver of this.maneuvers) {
			this.conditionalObKeysByManeuver.set(
				maneuver.maneuverCode,
				buildConditionalObKeySet(assessmentConfig, maneuver.maneuverCode),
			);
		}
	}
}
